/**
 * CreativePipeline — Kanban board for tracking ad creatives
 * through the entire lifecycle: idea → testing → winner/loser.
 *
 * Persists pipeline state per-user via localStorage.
 * Integrates with campaign launches to auto-move creatives.
 */
import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useStore } from "@/lib/store";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import {
  KanbanBoard,
  type KanbanColumn,
  type KanbanTask,
} from "@/components/ui/trello-kanban-board";
import {
  Workflow,
  RotateCcw,
  Rocket,
  CheckSquare,
  Download,
  Loader2,
  Search,
  X,
} from "lucide-react";

// ── Column definitions ────────────────────────────────────

const PIPELINE_COLUMN_IDS = [
  "pomysly",
  "do-nagrania",
  "do-montazu",
  "gotowe-do-testu",
  "w-trakcie-testu",
  "zwycieskie",
  "niedzialajace",
  "przestalo-dzialac",
] as const;

type PipelineColumnId = (typeof PIPELINE_COLUMN_IDS)[number];

const COLUMN_LABEL_KEYS: Record<PipelineColumnId, string> = {
  pomysly: "creativePipeline.column.pomysly",
  "do-nagrania": "creativePipeline.column.doNagrania",
  "do-montazu": "creativePipeline.column.doMontazu",
  "gotowe-do-testu": "creativePipeline.column.gotoweDoTestu",
  "w-trakcie-testu": "creativePipeline.column.wTrakcieTestu",
  zwycieskie: "creativePipeline.column.zwycieskie",
  niedzialajace: "creativePipeline.column.niedzialajace",
  "przestalo-dzialac": "creativePipeline.column.przestaloDzialac",
};

const COLUMN_COLORS: Record<string, string> = {
  pomysly: "bg-slate-500",
  "do-nagrania": "bg-blue-500",
  "do-montazu": "bg-indigo-500",
  "gotowe-do-testu": "bg-cyan-500",
  "w-trakcie-testu": "bg-amber-500",
  zwycieskie: "bg-emerald-500",
  niedzialajace: "bg-red-500",
  "przestalo-dzialac": "bg-orange-500",
};

const LABEL_COLORS: Record<string, string> = {
  tekst: "bg-blue-500",
  grafika: "bg-pink-500",
  video: "bg-violet-500",
  winner: "bg-emerald-500",
  loser: "bg-red-500",
  paused: "bg-amber-500",
};

const STORAGE_KEY_PREFIX = "creative-pipeline-columns";
const SYNCED_IDS_KEY_PREFIX = "creative-pipeline-synced-ids";
const DEFAULT_PIPELINE_SCOPE = "global";
const PIPELINE_UPDATED_EVENT = "pipeline-updated";

interface LibraryGraphic {
  id: string;
  filename: string;
  format: string;
  mediaType: "image" | "video" | null;
  signedUrl: string | null;
  createdAt: string;
}

interface LibraryResponse {
  items: LibraryGraphic[];
  total: number;
}

// ── Helpers ───────────────────────────────────────────────

const buildEmptyColumns = (): KanbanColumn[] =>
  PIPELINE_COLUMN_IDS.map((id) => ({
    id,
    title: COLUMN_LABEL_KEYS[id],
    tasks: [],
  }));

const buildScopedStorageKey = (prefix: string, scopeKey: string): string => `${prefix}:${scopeKey}`;

const getPipelineStorageKey = (scopeKey: string): string =>
  buildScopedStorageKey(STORAGE_KEY_PREFIX, scopeKey);

const getSyncedIdsStorageKey = (scopeKey: string): string =>
  buildScopedStorageKey(SYNCED_IDS_KEY_PREFIX, scopeKey);

const loadColumns = (scopeKey: string = DEFAULT_PIPELINE_SCOPE): KanbanColumn[] => {
  try {
    const raw = localStorage.getItem(getPipelineStorageKey(scopeKey));
    if (!raw) return buildEmptyColumns();
    const parsed = JSON.parse(raw) as KanbanColumn[];
    // Ensure all columns exist (in case new ones were added)
    const existingMap = new Map(parsed.map((c) => [c.id, c]));
    return PIPELINE_COLUMN_IDS.map(
      (id) => existingMap.get(id) ?? { id, title: COLUMN_LABEL_KEYS[id], tasks: [] }
    );
  } catch {
    return buildEmptyColumns();
  }
};

const saveColumns = (cols: KanbanColumn[], scopeKey: string = DEFAULT_PIPELINE_SCOPE) => {
  localStorage.setItem(getPipelineStorageKey(scopeKey), JSON.stringify(cols));
};

const loadSyncedIds = (scopeKey: string = DEFAULT_PIPELINE_SCOPE): Set<string> => {
  try {
    const raw = localStorage.getItem(getSyncedIdsStorageKey(scopeKey));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
};

const saveSyncedIds = (ids: Set<string>, scopeKey: string = DEFAULT_PIPELINE_SCOPE) => {
  localStorage.setItem(getSyncedIdsStorageKey(scopeKey), JSON.stringify([...ids]));
};

const getAllTaskIds = (cols: KanbanColumn[]): Set<string> => {
  const ids = new Set<string>();
  for (const col of cols) {
    for (const task of col.tasks) {
      ids.add(task.id);
    }
  }
  return ids;
};

const graphicToLabel = (g: LibraryGraphic): string => {
  if (g.mediaType === "video") return "video";
  return "grafika";
};

const graphicToTask = (g: LibraryGraphic): KanbanTask => ({
  id: `lib-${g.id}`,
  title: g.filename || g.format,
  labels: [graphicToLabel(g)],
  imageUrl: g.signedUrl ?? undefined,
  createdAt: g.createdAt,
});

// ── Public API for other components ───────────────────────

/**
 * Move a task (creative) to a specific column.
 * Used by CampaignBuilder to move accepted creatives → "w-trakcie-testu".
 */
export const moveCreativeToPipelineColumn = (
  taskId: string,
  targetColumnId: PipelineColumnId,
  scopeKey: string = DEFAULT_PIPELINE_SCOPE
): void => {
  const cols = loadColumns(scopeKey);
  let movedTask: KanbanTask | undefined;

  const updated = cols.map((col) => {
    const found = col.tasks.find((t) => t.id === taskId);
    if (found) {
      movedTask = found;
      return { ...col, tasks: col.tasks.filter((t) => t.id !== taskId) };
    }
    return col;
  });

  if (!movedTask) return;

  const finalCols = updated.map((col) =>
    col.id === targetColumnId ? { ...col, tasks: [...col.tasks, movedTask!] } : col
  );

  saveColumns(finalCols, scopeKey);
  // Dispatch event for the pipeline component to re-read
  window.dispatchEvent(new CustomEvent(PIPELINE_UPDATED_EVENT, { detail: { scopeKey } }));
};

/**
 * Add a new creative to a specific pipeline column.
 * Used when AI generates ad scripts/graphics → auto-lands in "pomysly".
 */
export const addCreativeToPipeline = (
  task: KanbanTask,
  columnId: PipelineColumnId = "pomysly",
  scopeKey: string = DEFAULT_PIPELINE_SCOPE
): void => {
  const cols = loadColumns(scopeKey);
  const updated = cols.map((col) =>
    col.id === columnId ? { ...col, tasks: [...col.tasks, task] } : col
  );
  saveColumns(updated, scopeKey);
  window.dispatchEvent(new CustomEvent(PIPELINE_UPDATED_EVENT, { detail: { scopeKey } }));
};

// ── Component ─────────────────────────────────────────────

interface MetaCreativeForPipeline {
  sourceType: "ad" | "image" | "video";
  id: string;
  name: string;
  description: string;
  mediaType: "image" | "video";
  thumbnailUrl: string | null;
}

const metaCreativeToTask = (c: MetaCreativeForPipeline): KanbanTask => ({
  id: `meta-${c.sourceType}-${c.id}`,
  title: c.name,
  description: c.description || undefined,
  labels: [c.mediaType === "video" ? "video" : "grafika"],
  imageUrl: c.thumbnailUrl ?? undefined,
  createdAt: new Date().toISOString(),
});

export default function CreativePipeline() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const pipelineScopeKey = useMemo(() => user?.id ?? DEFAULT_PIPELINE_SCOPE, [user?.id]);
  const [columns, setColumns] = useState<KanbanColumn[]>(buildEmptyColumns);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [isImportingMeta, setIsImportingMeta] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { setPendingCampaignCreatives, setCurrentModule, selectedClientUserId } = useStore();

  const handleImportFromMeta = useCallback(async () => {
    setIsImportingMeta(true);
    try {
      const params = new URLSearchParams();
      if (selectedClientUserId) params.set("forUserId", selectedClientUserId);
      const qs = params.toString();
      const url = `/api/integrations/meta/all-creatives${qs ? `?${qs}` : ""}`;
      const data = (await apiRequest("GET", url)) as {
        creatives: MetaCreativeForPipeline[];
      };

      if (!data.creatives?.length) {
        toast({
          title: t("creativePipeline.toast.noCreatives"),
          description: t("creativePipeline.toast.noCreativesDesc"),
        });
        return;
      }

      const cols = loadColumns(pipelineScopeKey);
      const existingIds = getAllTaskIds(cols);

      const newTasks = data.creatives.map(metaCreativeToTask).filter((t) => !existingIds.has(t.id));

      if (newTasks.length === 0) {
        toast({
          title: t("creativePipeline.toast.alreadyImported"),
          description: t("creativePipeline.toast.alreadyImportedDesc"),
        });
        return;
      }

      const updated = cols.map((col) =>
        col.id === "gotowe-do-testu" ? { ...col, tasks: [...col.tasks, ...newTasks] } : col
      );

      saveColumns(updated, pipelineScopeKey);
      setColumns(updated);
      toast({
        title: t("creativePipeline.toast.imported"),
        description: t("creativePipeline.toast.importedDesc", { count: newTasks.length }),
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("creativePipeline.unknownError");
      toast({
        title: t("creativePipeline.toast.tryAgain"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsImportingMeta(false);
    }
  }, [pipelineScopeKey, selectedClientUserId, toast, t]);

  useEffect(() => {
    setColumns(loadColumns(pipelineScopeKey));
    setSelectedTaskIds(new Set());
  }, [pipelineScopeKey]);

  // Fetch library creatives for auto-sync
  const { data: libraryData } = useQuery<LibraryResponse>({
    queryKey: ["graphics-library-pipeline-sync", pipelineScopeKey],
    queryFn: () => apiRequest("GET", "/api/graphics?page=1&pageSize=200"),
    staleTime: 30_000,
    retry: 2,
  });

  // Auto-sync: add library creatives to "pomysly" if not already in pipeline
  useEffect(() => {
    if (!libraryData?.items?.length) return;

    const cols = loadColumns(pipelineScopeKey);
    const existingIds = getAllTaskIds(cols);
    const syncedIds = loadSyncedIds(pipelineScopeKey);

    const newGraphics = libraryData.items.filter((g) => {
      const taskId = `lib-${g.id}`;
      return !existingIds.has(taskId) && !syncedIds.has(g.id);
    });

    if (newGraphics.length === 0) return;

    const newTasks = newGraphics.map(graphicToTask);
    const updated = cols.map((col) =>
      col.id === "pomysly" ? { ...col, tasks: [...col.tasks, ...newTasks] } : col
    );

    // Track synced IDs so deleted pipeline items don't reappear
    const updatedSyncedIds = new Set(syncedIds);
    for (const g of newGraphics) {
      updatedSyncedIds.add(g.id);
    }
    saveSyncedIds(updatedSyncedIds, pipelineScopeKey);

    saveColumns(updated, pipelineScopeKey);
    setColumns(updated);
  }, [libraryData, pipelineScopeKey]);

  // Listen for external pipeline updates
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ scopeKey?: string }>;
      const eventScopeKey = customEvent.detail?.scopeKey ?? DEFAULT_PIPELINE_SCOPE;
      if (eventScopeKey !== pipelineScopeKey) {
        return;
      }
      setColumns(loadColumns(pipelineScopeKey));
    };
    window.addEventListener(PIPELINE_UPDATED_EVENT, handler);
    return () => window.removeEventListener(PIPELINE_UPDATED_EVENT, handler);
  }, [pipelineScopeKey]);

  const handleColumnsChange = useCallback(
    (newCols: KanbanColumn[]) => {
      setColumns(newCols);
      saveColumns(newCols, pipelineScopeKey);
    },
    [pipelineScopeKey]
  );

  const handleTaskMove = useCallback((taskId: string, fromColumnId: string, toColumnId: string) => {
    // Could add analytics tracking here in the future
  }, []);

  const handleTaskAdd = useCallback(
    (columnId: string, title: string) => {
      // Re-save after the board adds a card
      const fresh = loadColumns(pipelineScopeKey);
      saveColumns(fresh, pipelineScopeKey);
    },
    [pipelineScopeKey]
  );

  const handleReset = useCallback(() => {
    const empty = buildEmptyColumns();
    setColumns(empty);
    saveColumns(empty, pipelineScopeKey);
    localStorage.removeItem(getSyncedIdsStorageKey(pipelineScopeKey));
    setSelectedTaskIds(new Set());
  }, [pipelineScopeKey]);

  const readyColumn = columns.find((c) => c.id === "gotowe-do-testu");
  const readyTasks = readyColumn?.tasks ?? [];
  const selectedCount = readyTasks.filter((t) => selectedTaskIds.has(t.id)).length;
  const allSelected = readyTasks.length > 0 && selectedCount === readyTasks.length;

  const handleTaskSelect = useCallback((taskId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelectedTaskIds(new Set());
      return;
    }
    setSelectedTaskIds(new Set(readyTasks.map((t) => t.id)));
  }, [allSelected, readyTasks]);

  const handleLaunchCampaign = useCallback(() => {
    const selected = readyTasks.filter((t) => selectedTaskIds.has(t.id));
    if (selected.length === 0) return;

    // Prepare creatives for campaign builder
    const creatives = selected.map((task) => {
      const isVideo = task.labels?.includes("video");
      return {
        url: task.imageUrl ?? "",
        name: task.title,
        type: (isVideo ? "video" : "image") as "image" | "video",
      };
    });

    // Move selected tasks to "w-trakcie-testu"
    const cols = loadColumns(pipelineScopeKey);
    const movedTasks: KanbanTask[] = [];
    const updated = cols.map((col) => {
      if (col.id === "gotowe-do-testu") {
        const remaining = col.tasks.filter((t) => {
          if (selectedTaskIds.has(t.id)) {
            movedTasks.push(t);
            return false;
          }
          return true;
        });
        return { ...col, tasks: remaining };
      }
      if (col.id === "w-trakcie-testu") {
        return { ...col, tasks: [...col.tasks, ...movedTasks] };
      }
      return col;
    });

    saveColumns(updated, pipelineScopeKey);
    setColumns(updated);
    setSelectedTaskIds(new Set());

    // Set pending creatives and switch to campaign builder
    setPendingCampaignCreatives(creatives);
    setCurrentModule("campaign-creator");
  }, [
    readyTasks,
    selectedTaskIds,
    setPendingCampaignCreatives,
    setCurrentModule,
    pipelineScopeKey,
  ]);

  const totalTasks = columns.reduce((sum, col) => sum + col.tasks.length, 0);

  const filteredColumns = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return columns;
    return columns.map((col) => ({
      ...col,
      tasks: col.tasks.filter(
        (t) => t.title.toLowerCase().includes(query) || t.description?.toLowerCase().includes(query)
      ),
    }));
  }, [columns, searchQuery]);

  const filteredTotalTasks = filteredColumns.reduce((sum, col) => sum + col.tasks.length, 0);
  const isSearchActive = searchQuery.trim().length > 0;

  const translatedColumns = useMemo(
    () =>
      filteredColumns.map((col) => ({
        ...col,
        title: t(COLUMN_LABEL_KEYS[col.id as PipelineColumnId] ?? col.title),
      })),
    [filteredColumns, t]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Workflow className="w-4 h-4 text-red-400" />
          <h2 className="text-sm font-semibold text-foreground">{t("creativePipeline.title")}</h2>
          <span className="text-[10px] text-muted-foreground bg-foreground/[0.06] px-1.5 py-0.5 rounded-full">
            {t("creativePipeline.tasksCount", {
              count: totalTasks,
              display: isSearchActive ? `${filteredTotalTasks}/${totalTasks}` : totalTasks,
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative flex items-center">
            <Search className="absolute left-2 w-3 h-3 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("creativePipeline.searchPlaceholder")}
              className="w-[180px] pl-7 pr-7 py-1 text-[11px] text-foreground placeholder:text-muted-foreground bg-background border border-border rounded-lg outline-none focus:border-ring transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-1.5 p-0.5 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <button
            onClick={handleImportFromMeta}
            disabled={isImportingMeta}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t("creativePipeline.importMetaTooltip")}
          >
            {isImportingMeta ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Download className="w-3 h-3" />
            )}
            {isImportingMeta ? t("creativePipeline.importing") : t("creativePipeline.importMeta")}
          </button>
          {totalTasks > 0 && (
            <button
              onClick={handleReset}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-white/30 hover:text-white/50 hover:bg-white/[0.04] rounded transition-colors"
              title={t("creativePipeline.resetTooltip")}
            >
              <RotateCcw className="w-3 h-3" />
              {t("creativePipeline.reset")}
            </button>
          )}
        </div>
      </div>

      {/* Selection toolbar */}
      {readyTasks.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/[0.06] bg-white/[0.02]">
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-white/50 hover:text-white/80 hover:bg-white/[0.04] rounded transition-colors"
          >
            <CheckSquare className="w-3.5 h-3.5" />
            {allSelected ? t("creativePipeline.deselectAll") : t("creativePipeline.selectAll")}
          </button>
          {selectedCount > 0 && (
            <button
              onClick={handleLaunchCampaign}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors"
            >
              <Rocket className="w-3.5 h-3.5" />
              {t("creativePipeline.launch", { count: selectedCount })}
            </button>
          )}
        </div>
      )}

      {/* Board */}
      <div className="flex-1 overflow-x-auto overflow-y-auto p-4">
        <KanbanBoard
          columns={translatedColumns}
          onColumnsChange={handleColumnsChange}
          onTaskMove={handleTaskMove}
          onTaskAdd={handleTaskAdd}
          columnColors={COLUMN_COLORS}
          labelColors={LABEL_COLORS}
          allowAddTask={true}
          selectableColumnIds={["gotowe-do-testu"]}
          selectedTaskIds={selectedTaskIds}
          onTaskSelect={handleTaskSelect}
          className="h-full"
        />
      </div>

      {/* Helper text */}
      <div className="px-4 py-2 border-t border-white/[0.04]">
        <p className="text-[10px] text-white/20 text-center">{t("creativePipeline.helper")}</p>
      </div>
    </div>
  );
}
